<template>
  <v-container
    grid-list-xl
    text-center
  >
    <v-layout wrap>
      <v-flex
        xs12
        sm12
        md12
        lg6
        offset-lg3
      >
        <v-card>
          <v-app-bar
            color="primary"
            flat
          >
            <v-toolbar-title class="text-body-2">
              {{ $t('components.giftPoints.title') }}
            </v-toolbar-title>
          </v-app-bar>

          <v-container
            fluid
            grid-list-md
          >
            <div
              v-for="(errorsGroup, errorsGroupIndex) in filteredErrors"
              :key="errorsGroupIndex"
              role="alert"
              aria-live="assertive"
            >
              <v-alert
                v-for="error in errorsGroup"
                :key="error.id"
                :type="error.type"
                :icon="error.icon"
              >
                {{ error.msg }}
              </v-alert>
            </div>

            <v-form>
              <v-layout>
                <v-flex xs12>
                  <v-text-field
                    v-model="form.charName"
                    v-validate="'required|min:1|max:36'"
                    prepend-icon="fad fa-helmet-battle"
                    color="accent"
                    data-vv-as="character name"
                    data-vv-name="charName"
                    :error-messages="errors.collect('charName')"
                    :label="$t('components.giftPoints.form.receiver')"
                    name="charName"
                    type="search"
                    aria-required="true"
                    :aria-invalid="!!errors.has('charName')"
                    @change="onSearchCharacter"
                  />
                </v-flex>
              </v-layout>

              <v-list
                two-line
                aria-label="Search Results"
              >
                <span
                  v-for="(item, index) in charactersList"
                  :key="item.account_oid"
                >
                  <v-list-item
                    ripple
                    @click="selectChar(item.name)"
                  >
                    <v-list-item-content class="gift-points-char-item">
                      <v-list-item-title>{{ item.name }}</v-list-item-title>
                      <v-list-item-subtitle class="text--accent">
                        {{ $t('components.giftPoints.level', { years: item.years }) }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider
                    v-if="index + 1 < charactersList.length"
                    :key="index"
                  />
                </span>
              </v-list>

              <v-layout>
                <v-flex xs12>
                  <v-text-field
                    v-model="form.points"
                    v-validate="'required|min:1|max:90000'"
                    prepend-icon="fad fa-coin"
                    color="accent"
                    data-vv-as="zushin points"
                    data-vv-name="points"
                    :error-messages="errors.collect('points')"
                    :label="$t('components.giftPoints.form.points')"
                    name="points"
                    type="number"
                    aria-required="true"
                    :aria-invalid="!!errors.has('points')"
                  />
                </v-flex>
              </v-layout>

              <v-progress-linear
                v-show="isLoading"
                color="accent"
                indeterminate
                aria-hidden="true"
              />

              <v-btn
                aria-label="Send Gift Points"
                color="accent"
                type="button"
                :disabled="!hasSelectedChar"
                @click.stop="setDialogVisiblity()"
              >
                {{ $t('components.giftPoints.buttons.send') }}
              </v-btn>

              <v-dialog
                v-model="isDialogVisible"
                transition="dialog-bottom-transition"
                :fullscreen="$vuetify.breakpoint.mdAndDown"
                :width="$vuetify.breakpoint.mdAndDown ? 'auto' : '700'"
                role="dialog"
                aria-modal="true"
              >
                <v-card>
                  <v-card-title class="h5">
                    {{ $t('components.giftPoints.dialog.title') }}
                  </v-card-title>

                  <v-card-text>
                    {{ $t('components.giftPoints.dialog.title', {
                      points: form.points,
                      charName: form.charName
                    }) }}
                  </v-card-text>

                  <v-card-actions>
                    <div class="flex-grow-1" />

                    <v-btn
                      aria-label="Decline Gift Points"
                      color="primary"
                      @click="setDialogVisiblity()"
                    >
                      {{ $t('components.giftPoints.dialog.decline') }}
                    </v-btn>

                    <v-btn
                      aria-label="Confirm Gift Points"
                      color="primary"
                      class="accent--text"
                      text
                      @click="validate()"
                    >
                      {{ $t('components.giftPoints.dialog.confirm') }}
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-form>
          </v-container>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import { ValidationProvider } from "vee-validate";
import { filterByKeys } from "@/utils/object.utils";
import Validator from "@/modules/validator.module";
import blacklist from "@/constants/blacklist";

const ValidatorModule = new Validator();

let searchCharTimeout = 0;

export default {
  name: "SendGiftPoints",
  components: {
    // eslint-disable-next-line vue/no-unused-components
    ValidationProvider
  },
  inject: ["$validator"],
  props: {
    zushinPoints: {
        type: Number,
        default: 0,
    },
  },
  data() {
    return {
        form: {
            charName: "",
            points: 300
        },
        charactersList: [],
        hasSelectedChar: false,
        isLoading: false,
        isDialogVisible: false
    }
  },
  computed: {
    filteredErrors() {
      return filterByKeys(
        this.$validator.errors.collect(),
        blacklist.forms.sendGiftPoints
      );
    }
  },
  methods: {
    onSearchCharacter(str) {
      if (this.charName !== str) {
        this.isLoading = true;
        this.charactersList = [];
        this.hasSelectedChar = false;
        clearTimeout(searchCharTimeout);
        searchCharTimeout = setTimeout(() => {
          this.$api.getGameCharactersFromString(str)
            .then(res => {
              this.isLoading = false;
              this.charactersList = res;
            })
            .catch(() => {
              this.isLoading = false;
            });
        }, 450);
      }
    },
    selectChar(name) {
      this.form.charName = name;
      this.charactersList = [];
      this.hasSelectedChar = true;
    },
    // Set dialog visiblity to true/false
    setDialogVisiblity() {
      this.isDialogVisible = !this.isDialogVisible;
    },
    validate() {
      this.isLoading = true;
      this.setDialogVisiblity();

        // Add to the validator
        ValidatorModule.add("send-gift-points", {
            errors: {
            points: {
                type: 0,
                msg: 'Disabled feature for the time being'
            }
            }
        });

      return;
      if (this.hasSelectedChar) {
        this.$validator.validateAll().then(result => {
          if (result) {
            this.$api.sendZushinPoints(this.form.charName, this.form.points)
              .then(res => {
                this.isLoading = false;
                // Add to the validator
                ValidatorModule.add("send-gift-points", res);
              })
              .catch(() => {
                this.isLoading = false;
              });
          } else {
            this.isLoading = false;
          }
        });
      } else {
        // Add to the validator
        ValidatorModule.add("send-gift-points", {
          errors: {
            points: {
              type: 0,
              msg: this.$t('components.giftPoints.errorCharNotFound')
            }
          }
        });
      }
    }
  }
};
</script>
